import { useEffect, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { ZoomIn, ZoomOut, Maximize2, RotateCcw, Check, X, Sparkles } from "lucide-react";

/**
 * Modal cropper — lets user zoom/pan/rotate a File before upload.
 * Two aspect modes: "square" (1:1) or "free" (contain the whole photo, no crop).
 *
 * Props:
 *   file: File (raw image from <input type=file>)
 *   onCancel: () => void
 *   onConfirm: (blob: Blob, previewUrl: string) => void
 */
export default function ImageCropModal({ file, onCancel, onConfirm }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [mode, setMode] = useState("square"); // square | portrait | free
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState(null);

  // Smart-suggest: pick the aspect that keeps the most subject visible.
  // Tall image → 9:16 PORTRAIT. Landscape → 1:1 (centered subject). ~Square → 1:1.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      const pick = ratio < 0.85 ? "portrait" : "square";
      setSuggested(pick);
      setMode(pick);
    };
    img.src = src;
  }, [src]);

  const onCropComplete = useCallback((_percentages, pixels) => setCroppedArea(pixels), []);

  const reset = () => { setZoom(1); setRotation(0); setCrop({ x: 0, y: 0 }); };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const blob = await produce({
        src,
        mode,
        rotation,
        croppedArea,
        fileType: file.type || "image/jpeg",
      });
      const previewUrl = URL.createObjectURL(blob);
      onConfirm(blob, previewUrl);
    } catch (e) {
      console.error("crop failed", e);
      onConfirm(file, src); // fall back to original
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-2 sm:p-4" data-testid="image-crop-modal">
      <div className="bg-white border-2 border-black brutal-shadow-lg w-full max-w-2xl flex flex-col max-h-[95vh]">
        <div className="bg-black text-[#D4FF00] p-3 flex items-center justify-between border-b-2 border-black">
          <div>
            <p className="font-mono-b text-[10px] uppercase tracking-widest opacity-70">EDIT PHOTO</p>
            <p className="font-display text-xl leading-none">CROP · ZOOM · ROTATE</p>
          </div>
          <button onClick={onCancel} data-testid="crop-close" className="border-2 border-[#D4FF00] p-1"><X className="w-4 h-4" /></button>
        </div>

        {/* Crop surface */}
        <div className="relative w-full flex-1 min-h-[280px] sm:min-h-[380px] bg-neutral-900" style={{ position: "relative" }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={mode === "square" ? 1 : mode === "portrait" ? 9 / 16 : undefined}
            objectFit={mode === "free" ? "contain" : "cover"}
            restrictPosition={mode !== "free"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            showGrid={mode !== "free"}
          />
        </div>

        {/* Controls */}
        <div className="p-3 border-t-2 border-black space-y-3 bg-white">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => { setMode("square"); reset(); }}
              data-testid="crop-mode-square"
              className={`px-3 py-1 border-2 border-black font-mono-b text-xs font-bold uppercase ${mode === "square" ? "bg-black text-[#D4FF00]" : "bg-white"}`}
            >
              1:1 SQUARE
            </button>
            <button
              type="button"
              onClick={() => { setMode("portrait"); reset(); }}
              data-testid="crop-mode-portrait"
              className={`px-3 py-1 border-2 border-black font-mono-b text-xs font-bold uppercase ${mode === "portrait" ? "bg-black text-[#D4FF00]" : "bg-white"}`}
            >
              9:16 PORTRAIT
            </button>
            <button
              type="button"
              onClick={() => { setMode("free"); reset(); }}
              data-testid="crop-mode-fit"
              className={`px-3 py-1 border-2 border-black font-mono-b text-xs font-bold uppercase inline-flex items-center gap-1 ${mode === "free" ? "bg-black text-[#D4FF00]" : "bg-white"}`}
            >
              <Maximize2 className="w-3 h-3" /> FIT WHOLE
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="crop-reset"
              className="ml-auto px-3 py-1 border-2 border-black bg-white font-mono-b text-xs font-bold uppercase inline-flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> RESET
            </button>
          </div>
          {suggested && (
            <p className="text-[10px] font-mono-b text-black/60 flex items-center gap-1" data-testid="crop-suggest">
              <Sparkles className="w-3 h-3" /> Smart pick: <b>{suggested === "portrait" ? "9:16 PORTRAIT" : "1:1 SQUARE"}</b> — best for this photo. Change anytime.
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
              data-testid="crop-zoom-out"
              className="border-2 border-black bg-white p-1 hover-nudge"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.05}
              value={zoom}
              data-testid="crop-zoom-slider"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
              aria-label="Zoom"
            />
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))}
              data-testid="crop-zoom-in"
              className="border-2 border-black bg-white p-1 hover-nudge"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <span className="font-mono-b text-xs w-12 text-right">{Math.round(zoom * 100)}%</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-mono-b text-xs">ROTATE</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              data-testid="crop-rotate"
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1"
              aria-label="Rotate"
            />
            <span className="font-mono-b text-xs w-12 text-right">{rotation}°</span>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              data-testid="crop-cancel"
              className="flex-1 border-2 border-black bg-white py-2 font-mono-b font-bold uppercase text-sm hover-nudge"
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              data-testid="crop-confirm"
              className="flex-1 bg-[#D4FF00] border-2 border-black py-2 font-mono-b font-bold uppercase text-sm hover-nudge inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> {busy ? "PROCESSING..." : "USE THIS"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Produce a Blob from the source image using canvas. */
async function produce({ src, mode, rotation, croppedArea, fileType }) {
  const img = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;

  if (mode === "free") {
    // "Fit whole" — return the image as-is (with rotation baked in). No crop.
    const canvas = document.createElement("canvas");
    // For rotated images, use the rotated bounding box
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const cw = img.width * cos + img.height * sin;
    const ch = img.width * sin + img.height * cos;
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#F4F4F0";
    ctx.fillRect(0, 0, cw, ch);
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate(rad);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    return blobFromCanvas(canvas, fileType);
  }

  // Cropped path (square or portrait) — respect the actual crop rectangle.
  // `croppedArea` is croppedAreaPixels from react-easy-crop, already in source-image pixels.
  const px = croppedArea || { x: 0, y: 0, width: img.width, height: img.height };
  const cropX = px.x;
  const cropY = px.y;
  const cropW = px.width;
  const cropH = px.height;
  if (!cropW || !cropH) {
    // Fallback: crop area not yet computed — bail out to full image
    const c = document.createElement("canvas");
    c.width = img.width;
    c.height = img.height;
    c.getContext("2d").drawImage(img, 0, 0);
    return blobFromCanvas(c, fileType);
  }

  // Render to a rotated safe canvas first, then crop
  const safeArea = Math.max(img.width, img.height) * 2;
  const stage = document.createElement("canvas");
  stage.width = safeArea;
  stage.height = safeArea;
  const stx = stage.getContext("2d");
  stx.translate(safeArea / 2, safeArea / 2);
  stx.rotate(rad);
  stx.drawImage(img, -img.width / 2, -img.height / 2);

  const out = document.createElement("canvas");
  out.width = Math.round(cropW);
  out.height = Math.round(cropH);
  const ox = out.getContext("2d");
  ox.drawImage(
    stage,
    safeArea / 2 - img.width / 2 + cropX,
    safeArea / 2 - img.height / 2 + cropY,
    cropW,
    cropH,
    0, 0,
    out.width, out.height,
  );
  return blobFromCanvas(out, fileType);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function blobFromCanvas(canvas, mime) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob failed"))), mime, 0.92);
  });
}
